import { Box, Button, InputAdornment, InputLabel, Link } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import {
  Backdrop,
  CheckBox,
  CloseIcon,
  EmailIconTwoTone,
  Input,
  InputPassword,
  LoadingSpinnerIconTwoTone,
  PasswordIconTwoTone,
  Typography,
  useNotificationModal,
} from '@room-match/shared-ui-components';
import { FrontLayout } from 'components';
import { FormikProps, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { COLORS } from 'shared/constants/COLORS';
import { useEmailValidation } from 'shared/hooks/useEmailValidation';
import { ISignUpRequestPayload } from 'shared/interfaces/IUser';
import { selectUserState, userSignup } from 'shared/redux/slicers/user.slicer';
import { getErrorMessage, PasswordPrinciple, validatePassword } from 'shared/utils/room-shared';
import * as yup from 'yup';
import { useStyles } from './Signup.styles';

import React from 'react';
import { ROUTES } from 'shared/constants/ROUTES';
import { useAppSelector } from 'shared/redux/store';
import ResetPasswordModal from './ResetPasswordModal';

// Login

const Signup = () => {
  const classes = useStyles();
  const { user: userState } = useAppSelector((state) => state);
  const dispatch = useDispatch();
  const history = useHistory();

  const [passwordValidationResult, setPasswordValidationResult] = useState<PasswordPrinciple | null>(null);
  const [password_str, setPasswordStr] = useState<string>('');
  const [emailErrMessage, setEmailErrMessage] = useState<string>('');
  const validateEmailAndPassword = (email: string, password: string) => {
    return true;
  };

  const { isEmailValidationLoading, isValidEmail, handleEmailChange, emailValue } = useEmailValidation();

  const [agree, setAgree] = useState(false);

  const [showModal, setshowModal] = useState(false);
  const { isLoading } = useSelector(selectUserState);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const emailValidation = () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(form.values.email);
  };

  const { handleOpen, isOpen, NotificationModal, handleSetMessage } = useNotificationModal({
    type: 'success',
  });

  // Forms
  const initialValues: ISignUpRequestPayload = {
    email: '',
    password: '',
    password_confirmation: '',
    user_type: 'talent',
    joined_via: 'web',
  };

  const [isFormComplete, setIsFormComplete] = useState(false);
  // const loginValues: ISignInRequestPayload = {

  // };

  // const signUpValidationSchema: yup.SchemaOf<ISignUpRequestPayload> = yup.object({
  const signUpValidationSchema: yup.SchemaOf<any> = yup.object({
    completed_primary_details: yup.boolean(),
    email: yup.string().email('Must be a valid email format').required('Email is required'),
    user_type: yup.string().required('User type is required'),
    password: yup
      .string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .test('passwordValidate', 'Invalid password', (value: any) => {
        if (value) {
          const validatePasswordResult = validatePassword(value);
          setPasswordValidationResult(validatePasswordResult);
          return validatePasswordResult.IsValid;
        }
        return false;
      }),
    password_confirmation: yup
      .string()
      .required('Password confirmation is required')
      .oneOf([yup.ref('password')], 'Your passwords do not match.'),
    joined_via: yup.string(),
  });
  // Handle sign up submit
  const handleSignUpSubmit = async (values: ISignUpRequestPayload) => {
    const { email, password } = values;

    if (!validateEmailAndPassword(email, password)) {
      // Display an error message to the user
      return;
    }

    dispatch(userSignup(values, history));
  };
  // Form
  const form: FormikProps<ISignUpRequestPayload> = useFormik({
    initialValues,
    validationSchema: signUpValidationSchema,
    validateOnBlur: true,
    onSubmit: (values, helpers) => {
      if (!isValidEmail) {
        helpers.setFieldError('email', 'Invalid Email');
      } else {
        setEmailErrMessage('');
        handleSignUpSubmit(values);
      }
    },
  });

  // Disable the button if the email or password is empty
  useEffect(() => {
    const isEmailValid = isValidEmail;
    const isPasswordValid =
      form.values.password !== '' &&
      form.values.password.length >= 8 && // Password should be at least 8 characters long
      /[A-Z]/.test(form.values.password) && // Contains uppercase letters
      /[a-z]/.test(form.values.password) && // Contains lowercase letters
      /[0-9]/.test(form.values.password) && // Contains numbers
      /[!@#$%^&*()_+[\]{}|;:'",.<>?~`\\/-]/.test(form.values.password); // Contains special characters

    setIsFormComplete(isEmailValid && isPasswordValid);
  }, [form.values.password, isValidEmail]);

  const handleSuccess = () => {
    setshowModal(false);
    handleSetMessage('Success', `A reset password link will be sent to your email address.`, 'OK');
    handleOpen();
  };

  useEffect(() => {
    if (userState.errorMessage === 'Email has already been taken') {
      setEmailErrMessage(
        `This email appears to already be linked to an account. If you have forgotten your password, please click 'Forgot Password' on the login page to reset it.`,
      );
    }
  }, [userState.errorMessage]);

  return (
    <FrontLayout
      newTalent
      heading="Register"
      subHeading="Let’s get started with your new access details below"
      containerWidth={480}
      type="register"
    >
      <Box component="div" className={classes.formContainer}>
        <InputLabel>Email Address</InputLabel>
        <Input
          autoComplete="off"
          className={`${classes.input} ${classes.errorMessageFix}`}
          name="email"
          type="email"
          onChange={(e) => {
            handleEmailChange(e.target.value);
            return form.handleChange(e);
          }}
          variant="outlined"
          placeholder="Enter Email..."
          fullWidth
          margin={'normal'}
          autoFocus
          // onBlur={(e) => {
          //   handleEmailChange(e.target.value);
          //   return form.handleBlur(e);
          // }}
          value={form.values.email}
          InputProps={{
            tabIndex: 1,
            startAdornment: (
              <InputAdornment position="start">
                <EmailIconTwoTone className={classes.startIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {emailValue && (
                  <>
                    {isEmailValidationLoading ? (
                      <LoadingSpinnerIconTwoTone style={{ fontSize: 40 }} />
                    ) : (
                      <>
                        {emailValidation() ? (
                          // Green if email is valid
                          <FiberManualRecordIcon
                            data-cy="email-indicator-valid"
                            style={{ fontSize: 12, color: COLORS.PASS_INDICATOR }}
                          />
                        ) : (
                          // Else Red
                          <FiberManualRecordIcon
                            data-cy="email-indicator-invalid"
                            style={{ fontSize: 12, color: COLORS.ERROR_INDICATOR }}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            ),
          }}
          inputProps={{ 'data-cy': 'email-field', tabIndex: 1 }}
          errorMessage={getErrorMessage(
            form.touched.email,
            form.errors.email
              ? form.errors.email
              : !isValidEmail && !isEmailValidationLoading
                ? 'Invalid Email'
                : emailErrMessage || userState.errorMessage || '',
          )}
        />
        <InputLabel style={{ marginTop: 10 }}>Create a password</InputLabel>
        <InputPassword
          autoComplete="off"
          className={classes.input}
          fullWidth
          name="password"
          onChange={(e) => {
            if (form.errors.password && !form.touched.password) {
              form.setFieldTouched('password');
              form.validateField('password');
            }
            setPasswordStr(e.target.value || '');
            form.setFieldValue('password_confirmation', e.target.value);
            return form.handleChange(e);
          }}
          errorMessage={getErrorMessage(form.touched.password, form.errors.password)}
          value={form.values.password}
          variant="outlined"
          placeholder="Type Password..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PasswordIconTwoTone className={classes.startIcon} />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-cy': 'password-field', tabIndex: 2 }}
          style={{ marginTop: '15px' }}
          onCopy={(e) => {
            e.preventDefault();
            return false;
          }}
          onPaste={(e) => {
            e.preventDefault();
            return false;
          }}
        />
        {/* <InputPassword
          className={classes.input}
          name="password_confirmation"
          fullWidth
          onChange={(e) => {
            if (form.errors.password_confirmation && !form.touched.password_confirmation) {
              form.setFieldTouched('password_confirmation');
              form.validateField('password_confirmation');
            }
            return form.handleChange(e);
          }}
          errorMessage={getErrorMessage(form.touched.password_confirmation, form.errors.password_confirmation)}
          value={form.values.password_confirmation}
          variant="outlined"
          placeholder="Confirm Password..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PasswordIconTwoTone className={classes.startIcon} />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ tabIndex: 2 }}
          style={{ marginTop: '15px' }}
          onCopy={(e) => {
            e.preventDefault();
            return false;
          }}
          onPaste={(e) => {
            e.preventDefault();
            return false;
          }}
        /> */}
        <Typography fontSize={14} className={classes.helpText} style={{ marginTop: 10 }}>
          *Password should be at least 8 characters long containing a combination of uppercase, lowercase, numbers and
          special characters
        </Typography>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 20 }}>
          <CheckBox
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setAgree(!agree);
            }}
            checked={agree}
            data-cy="privacy-policy-checkbox"
            name="privacy"
            id="privacy"
            data-testid="privacy-checkbox"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
          <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography>I agree to the</Typography>
            <Link
              href="https://casting.com/terms/"
              style={{
                marginLeft: 5,
                fontSize: 17,
                color: COLORS.PRIMARY,
                cursor: 'pointer',
                background: 'transparent',
                padding: 0,
              }}
              target="_blank"
            >
              Terms
            </Link>
            <Typography style={{ marginLeft: 10 }}> and </Typography>
            <Link
              href="https://casting.com/privacy-policy-2/"
              style={{
                marginLeft: 5,
                fontSize: 17,
                color: COLORS.PRIMARY,
                cursor: 'pointer',
                background: 'transparent',
                padding: 0,
              }}
              target="_blank"
            >
              Privacy Policy
            </Link>
          </Box>
        </Box>
        {/* <Box style={{ display: 'flex', alignItems: 'center' }}>
          <CheckBox
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setAgree({ ...agree, terms: e.target.checked });
            }}
            checked={agree.terms}
            data-cy="terms-checkbox"
            name="terms"
            id="terms"
            data-testid="terms-checkbox"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
          <Typography>I read and agree to </Typography>
          <Link
            href="https://casting.com/terms/"
            style={{
              marginLeft: 5,
              fontSize: 17,
              color: COLORS.PRIMARY,
              cursor: 'pointer',
              background: 'transparent',
              padding: 0,
            }}
            target="_blank"
          >
            Terms of Service
          </Link>
        </Box> */}
        <Button
          disabled={!isFormComplete || isEmailValidationLoading || !agree}
          color="primary"
          variant="contained"
          disableElevation
          fullWidth
          className={classes.buttonCls}
          onClick={() => form.handleSubmit()}
          data-cy="button-continue"
        >
          Continue
        </Button>
        <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <Typography>Already have an account?</Typography>
          <Link data-cy="signup-login-button" href="/login">
            Login
          </Link>
        </Box>
        <Box style={{ display: 'flex', justifyContent: 'center', textAlign: 'center', marginTop: 10 }}>
          <Typography fontSize={13} color="mainGrey">
            If you have an existing account and would like to add your representation, please log in and navigate to the
            'representatives' tab to the left to add any/all members of your team.
          </Typography>
        </Box>
      </Box>
      <Backdrop isLoading={isLoading} />
      {showModal ? (
        <ResetPasswordModal
          handleSuccess={handleSuccess}
          open={showModal}
          close={() => setshowModal(false)}
          email={form.values.email}
        />
      ) : (
        <React.Fragment />
      )}
      {isOpen && <NotificationModal onClose={() => history.push(ROUTES.LOGIN)} />}
    </FrontLayout>
  );
};

export default Signup;
